import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Switch } from '../ui/Switch';
import AppIcon from '../AppIcon';
import { cn } from '../../utils/cn';
import type {
  NotificationPreferenceView,
  CompanyNotificationSettingView,
  NotificationTypeName,
} from '../../api/General/NotificationPreference';

// --- Constants ---

// Keys below are NotificationType.Name values, typed against the
// `NotificationTypeName` union in api/General/NotificationPreference.ts (which
// mirrors the server seed in Mowazaf.Server/Data/BluHRMSContext.cs plus the ids
// inserted directly by migrations). Both maps below are TOTAL over that union
// and enforced at compile time, so a newly seeded type that nobody categorised
// or labelled is a BUILD ERROR — not a silent fall-through into "General" with
// its raw internal name showing, which is what this file used to do.

export const NOTIFICATION_CATEGORIES = {
  Attendance: [
    'ClockInReminder',
    'ClockOutReminder',
    'BreakInNotification',
    'BreakOutNotification',
    'StillInBreakVerification',
    'UsersMissingClockInNotification',
    'EarlyClockOutNotification',
    'OutOfBoundsClock',
  ],
  'Schedule & Shifts': ['ScheduleChanged', 'ShiftSwapRequested', 'ShiftSwapDecision'],
  'Timesheets & Overtime': [
    'TimeSheetsCreated',
    'TimesheetSubmitted',
    'TimesheetDecision',
    'OvertimeVerification',
    'OvertimeApprovalRequest',
    'OvertimeApprovalResponse',
    'ShiftPayApprovalDigest',
    'ShiftPayDecided',
  ],
  'Leave & Holidays': [
    'LeaveRequest',
    'LeaveRequestResponse',
    'LeaveRequestUndoResponse',
    'LeaveApprovalStepPending',
    'LeaveApprovalStepProgress',
    'LeaveApprovalEscalated',
    'LeaveAutoApproved',
    'LeaveCancellationRequest',
    'LeaveCancellationDecision',
    'RecurringLeaveSubmitted',
    'RecurringLeaveCancelled',
    'ShortLeaveRequest',
    'ShortLeaveDecision',
    'ShortLeaveWithdrawn',
    'EncashmentRequest',
    'EncashmentDecision',
    'EncashmentWithdrawn',
    'CompOffCreditUpdated',
    'ReturnToWorkRequired',
    'ReturnToWorkDocumentsUploaded',
    'ReturnToWorkCompleted',
    'CarryForwardExpiring',
    'HolidayNotification',
    'RestrictedHolidaySelectionWindow',
    'LeaveBlackout',
  ],
  'Approvals & Delegation': [
    'AttendanceCorrectionRequestSubmitted',
    'AttendanceCorrectionRequestDecision',
    'OutOfBoundsApprovalRequested',
    'OutOfBoundsApprovalDecision',
    'DelegationUpdated',
  ],
  'Payroll & Claims': [
    'UserMonthlyPaySlip',
    'ClaimSubmitted',
    'ClaimDecision',
    'ClaimPaid',
    'GpssaContributionsPending',
    'KsaMudadDeadlineReminder',
    'KsaPayrollRunReminder',
    'PayrollPaid',
  ],
  Assets: [
    'AssetRequestSubmitted',
    'AssetRequestDecision',
    'AssetRequestFulfilled',
    'AssetOtpVerification',
    'AssetReplenishment',
  ],
  'Employee Lifecycle': [
    'OnboardingStarted',
    'OnboardingStartDateReminder',
    'OffboardingStarted',
    'OffboardingLastDayReminder',
    'InternalMovementInitiated',
    'InternalMovementPendingManager',
    'InternalMovementPendingHr',
    'InternalMovementDecision',
    'InternalMovementEffectiveDateReminder',
    'EmployeeMovementSubmitted',
    'EmployeeMovementDecision',
    'BusinessRoleAssigned',
    'BusinessRoleRevoked',
    'PublicProfileSubmitted',
    'PublicProfileDecision',
    'UserDeletedNotice',
    'PublicProfileSetupNudge',
    'AccountInactivityReminder',
    'AccountInactivityFinalNotice',
  ],
  'Documents & Compliance': [
    'DocumentExpiryReminder',
    'DocumentExpired',
    'CchiExpiryReminder',
    'DependentInsuranceUpdated',
  ],
  'Performance & Engagement': [
    'PerformanceSnapshotSubmitted',
    'PerformanceSnapshotAcknowledged',
    'GoalBlockerUpdated',
    'ReflectionManagerResponse',
    'MentorAssignmentUpdated',
    'CheckInUpdated',
    'CheckInReminder',
    'CheckInReminderDigest',
    'RecognitionGapReminder',
    'Q12Nudge',
  ],
  Projects: [
    'WorkspaceStaffingRequired',
    'WorkspaceMilestoneBilling',
    'WorkspaceMilestoneOverdue',
    'TaskAssigned',
    'TaskSlaBreached',
    'ChecklistApprovalRequested',
    'ChecklistItemRejected',
    'TaskCommentAdded',
    'TaskDueToday',
    'TaskMentioned',
  ],
  Tournaments: ['Tournament'],
  Subscription: [
    'SubscriptionTrialEndingSoon',
    'SubscriptionTrialExpired',
    'SubscriptionPaidRenewalDue',
    'SubscriptionPaidOverdue',
    'SubscriptionUpgradeRequest',
    'SubscriptionHrUpgradeNudge',
    'PlatformSubscriptionActivity',
    // Platform, Operator-only like the line above; filed here because this is the group that
    // already holds the Operator's platform type. Only the Operator's own page lists it.
    'MailQueueAlert',
  ],
  Billing: [
    'BillingCheckoutStarted',
    'BillingPaymentSucceeded',
    'BillingPaymentFailed',
    'BillingDunningReminder',
    'BillingSubscriptionActivated',
    'BillingSubscriptionRenewed',
    'BillingSubscriptionCancelled',
    'BillingPlanChanged',
    'BillingInvoiceIssued',
    'BillingSeatCountChanged',
    'BillingAccessRestricted',
    'BillingCardExpiringSoon',
  ],
  General: ['OnDemandNotification'],
} as const satisfies Record<string, readonly NotificationTypeName[]>;

export type NotificationCategory = keyof typeof NOTIFICATION_CATEGORIES;

/**
 * Compile-time exhaustiveness gate for the category map.
 *
 * A `Record<NotificationTypeName, ...>` can't express "category -> list of
 * types", so totality is asserted the other way round: subtract every
 * categorised name from the union and require the remainder to be `never`. If a
 * seeded type is added to `NotificationTypeName` and nobody files it under a
 * category, this assignment fails to compile and the error message NAMES the
 * uncategorised types. Do not "fix" it by deleting this block.
 */
type CategorisedTypeName =
  (typeof NOTIFICATION_CATEGORIES)[NotificationCategory][number];
type UncategorisedNotificationTypes = Exclude<
  NotificationTypeName,
  CategorisedTypeName
>;
const _everyNotificationTypeIsCategorised: [
  UncategorisedNotificationTypes
] extends [never]
  ? true
  : UncategorisedNotificationTypes[] = true;
void _everyNotificationTypeIsCategorised;

/**
 * Types a plain employee never receives; hidden from their preference matrix.
 * Intentionally PARTIAL (it is a subset, not a classification of every type),
 * so it is only typo-checked against the union — not exhaustive.
 */
const HR_ADMIN_ONLY_TYPE_NAMES = [
  'CchiExpiryReminder',
  'KsaMudadDeadlineReminder',
  'KsaPayrollRunReminder',
  'GpssaContributionsPending',
  'SubscriptionTrialEndingSoon',
  'SubscriptionTrialExpired',
  'SubscriptionPaidRenewalDue',
  'SubscriptionPaidOverdue',
  'SubscriptionUpgradeRequest',
  'SubscriptionHrUpgradeNudge',
] as const satisfies readonly NotificationTypeName[];

export const HR_ADMIN_ONLY_TYPES: ReadonlySet<string> = new Set<string>(
  HR_ADMIN_ONLY_TYPE_NAMES
);

/**
 * TOTAL Record on purpose — same contract as `DISPATCHABLE_BY_SOURCE` in
 * components/pendingActions/dispatch.ts. A new `NotificationTypeName` with no
 * label here is a build error rather than a row rendering the raw server name.
 */
export const NOTIFICATION_TYPE_LABELS: Record<NotificationTypeName, string> = {
  // Attendance
  ClockInReminder: 'Clock In Reminder',
  ClockOutReminder: 'Clock Out Reminder',
  BreakInNotification: 'Break In Reminder',
  BreakOutNotification: 'Break Out Reminder',
  StillInBreakVerification: 'Still On Break Check',
  UsersMissingClockInNotification: 'Missed Clock In',
  EarlyClockOutNotification: 'Early Clock Out',
  OutOfBoundsClock: 'Clocked Outside Allowed Area',
  // Schedule & shifts
  ScheduleChanged: 'Schedule Changed',
  ShiftSwapRequested: 'Shift Swap Requested',
  ShiftSwapDecision: 'Shift Swap Decision',
  // Timesheets & overtime
  TimeSheetsCreated: 'Timesheets Created',
  TimesheetSubmitted: 'Timesheet Submitted',
  TimesheetDecision: 'Timesheet Decision',
  OvertimeVerification: 'Overtime Verification',
  OvertimeApprovalRequest: 'Overtime Approval Request',
  OvertimeApprovalResponse: 'Overtime Approval Decision',
  ShiftPayApprovalDigest: 'Shift Pay Approval Request',
  ShiftPayDecided: 'Shift Pay Decision',
  // Leave & holidays
  LeaveRequest: 'Leave Request',
  LeaveRequestResponse: 'Leave Decision',
  LeaveRequestUndoResponse: 'Leave Decision Undone',
  LeaveApprovalStepPending: 'Leave Approval Step Pending',
  LeaveApprovalStepProgress: 'Leave Approval Progress',
  LeaveApprovalEscalated: 'Leave Approval Escalated',
  LeaveAutoApproved: 'Leave Approved Automatically',
  LeaveCancellationRequest: 'Leave Cancellation Request',
  LeaveCancellationDecision: 'Leave Cancellation Decision',
  RecurringLeaveSubmitted: 'Recurring Leave Submitted',
  RecurringLeaveCancelled: 'Recurring Leave Cancelled',
  ShortLeaveRequest: 'Short Leave Request',
  ShortLeaveDecision: 'Short Leave Decision',
  ShortLeaveWithdrawn: 'Short Leave Withdrawn',
  EncashmentRequest: 'Encashment Request',
  EncashmentDecision: 'Encashment Decision',
  EncashmentWithdrawn: 'Encashment Withdrawn',
  CompOffCreditUpdated: 'Comp-Off Balance Updated',
  ReturnToWorkRequired: 'Return To Work Check-In',
  ReturnToWorkDocumentsUploaded: 'Return To Work Documents',
  ReturnToWorkCompleted: 'Return To Work Completed',
  CarryForwardExpiring: 'Carry-Forward Expiring',
  HolidayNotification: 'Holiday Announcement',
  RestrictedHolidaySelectionWindow: 'Restricted Holiday Selection',
  LeaveBlackout: 'Blackout Period',
  // Approvals & delegation
  AttendanceCorrectionRequestSubmitted: 'Correction Submitted',
  AttendanceCorrectionRequestDecision: 'Correction Decision',
  OutOfBoundsApprovalRequested: 'Out Of Bounds Approval Request',
  OutOfBoundsApprovalDecision: 'Out Of Bounds Approval Decision',
  DelegationUpdated: 'Approval Delegation Updated',
  // Payroll & claims
  UserMonthlyPaySlip: 'Payslip Generated',
  ClaimSubmitted: 'Claim Submitted',
  ClaimDecision: 'Claim Decision',
  ClaimPaid: 'Claim Paid',
  GpssaContributionsPending: 'GPSSA Contributions Pending',
  KsaMudadDeadlineReminder: 'Mudad Deadline Reminder',
  KsaPayrollRunReminder: 'KSA Payroll Run Reminder',
  // Assets
  AssetRequestSubmitted: 'Asset Request Submitted',
  AssetRequestDecision: 'Asset Request Decision',
  AssetRequestFulfilled: 'Asset Issued',
  AssetOtpVerification: 'Asset Verification Code',
  AssetReplenishment: 'Asset Replenishment Reminder',
  // Employee lifecycle
  OnboardingStarted: 'Onboarding Started',
  OnboardingStartDateReminder: 'Onboarding Start Reminder',
  OffboardingStarted: 'Offboarding Started',
  OffboardingLastDayReminder: 'Last Working Day Reminder',
  InternalMovementInitiated: 'Internal Movement Initiated',
  InternalMovementPendingManager: 'Internal Movement Needs Manager',
  InternalMovementPendingHr: 'Internal Movement Needs HR',
  InternalMovementDecision: 'Internal Movement Decision',
  InternalMovementEffectiveDateReminder: 'Internal Movement Reminder',
  EmployeeMovementSubmitted: 'Employee Movement Submitted',
  EmployeeMovementDecision: 'Employee Movement Decision',
  BusinessRoleAssigned: 'Business Role Assigned',
  BusinessRoleRevoked: 'Business Role Revoked',
  PublicProfileSubmitted: 'Public Profile Submitted',
  PublicProfileDecision: 'Public Profile Decision',
  PublicProfileSetupNudge: 'Public Profile Setup Reminder',
  AccountInactivityReminder: 'Account Inactivity Reminder',
  AccountInactivityFinalNotice: 'Account Closure Final Notice',
  // Documents & compliance
  DocumentExpiryReminder: 'Document Expiry Reminder',
  DocumentExpired: 'Document Expired',
  CchiExpiryReminder: 'CCHI Policy Expiry',
  DependentInsuranceUpdated: 'Dependent Insurance Updated',
  // Performance & engagement
  PerformanceSnapshotSubmitted: 'Performance Snapshot Ready',
  PerformanceSnapshotAcknowledged: 'Performance Snapshot Acknowledged',
  GoalBlockerUpdated: 'Goal Blocker Update',
  ReflectionManagerResponse: 'Reflection Response',
  MentorAssignmentUpdated: 'Mentoring Pairing Updated',
  CheckInUpdated: '1:1 Check-In Updated',
  CheckInReminder: '1:1 Check-In Reminder',
  CheckInReminderDigest: '1:1 Check-In Daily Digest',
  RecognitionGapReminder: 'Recognition Gap Reminder',
  Q12Nudge: 'Team Engagement Nudge',
  // Projects
  WorkspaceStaffingRequired: 'Workspace Staffing Required',
  WorkspaceMilestoneBilling: 'Workspace Milestone Billing',
  WorkspaceMilestoneOverdue: 'Workspace Milestone Overdue',
  TaskAssigned: 'Task Assigned',
  TaskSlaBreached: 'Task SLA Breached',
  ChecklistApprovalRequested: 'Checklist Item Awaiting Approval',
  ChecklistItemRejected: 'Checklist Item Rejected',
  TaskCommentAdded: 'New Comment on a Task',
  TaskDueToday: 'Tasks Due Today',
  TaskMentioned: 'Task Mentioned',
  // Tournaments
  Tournament: 'Tournament Update',
  // Subscription
  SubscriptionTrialEndingSoon: 'Trial Ending Soon',
  SubscriptionTrialExpired: 'Trial Expired',
  SubscriptionPaidRenewalDue: 'Renewal Due',
  SubscriptionPaidOverdue: 'Payment Overdue',
  SubscriptionUpgradeRequest: 'Upgrade Request',
  SubscriptionHrUpgradeNudge: 'Upgrade Request From Your Team',
  PlatformSubscriptionActivity: 'Tenant Subscription Activity',
  MailQueueAlert: 'Mail Queue Alerts',
  // Billing
  BillingCheckoutStarted: 'Checkout Started',
  BillingPaymentSucceeded: 'Payment Received',
  BillingPaymentFailed: 'Payment Failed',
  BillingDunningReminder: 'Payment Reminder',
  BillingSubscriptionActivated: 'Subscription Activated',
  BillingSubscriptionRenewed: 'Subscription Renewed',
  BillingSubscriptionCancelled: 'Subscription Cancelled',
  BillingPlanChanged: 'Plan Changed',
  BillingInvoiceIssued: 'Invoice Issued',
  BillingSeatCountChanged: 'Seat Count Changed',
  BillingAccessRestricted: 'Access Restricted',
  BillingCardExpiringSoon: 'Card Expiring Soon',
  // CO-375 / E13
  PayrollPaid: 'Salary Paid',
  UserDeletedNotice: 'Employee Account Deleted',
  // General
  OnDemandNotification: 'Custom Notification',
};

// --- Types ---

interface NotificationPreferenceMatrixProps {
  /** User mode: show preferences with toggles */
  preferences?: NotificationPreferenceView[];
  onToggle?: (
    notificationTypeId: number,
    channel: 'email' | 'push' | 'inApp',
    value: boolean
  ) => void;

  /** Admin mode: show company settings with force-disable */
  companySettings?: CompanyNotificationSettingView[];
  onCompanySettingChange?: (
    notificationTypeId: number,
    field: string,
    value: boolean
  ) => void;

  /** Common */
  disabled?: boolean;
  readOnly?: boolean;
  mode: 'user' | 'admin';
  userRole?: string;
}

type Channel = 'email' | 'push' | 'inApp';

const CHANNELS: { key: Channel; label: string; masterLabel: string }[] = [
  { key: 'email', label: 'Email', masterLabel: 'All Email' },
  { key: 'push', label: 'Push', masterLabel: 'All Push' },
  { key: 'inApp', label: 'In-App', masterLabel: 'All In-App' },
];

// --- Helpers ---

/** Map admin field names to channel keys */
function adminFieldForChannel(channel: Channel): string {
  switch (channel) {
    case 'email':
      return 'emailsDisabled';
    case 'push':
      return 'mobilePushNotificationsDisabled';
    case 'inApp':
      return 'inAppDisabled';
  }
}

/** Organize items by category, putting unknowns into General */
function categorize<T extends { notificationTypeName: string }>(
  items: T[]
): { category: string; items: T[] }[] {
  const byName = new Map<string, T>();
  for (const item of items) {
    byName.set(item.notificationTypeName, item);
  }

  const result: { category: string; items: T[] }[] = [];

  for (const [category, typeNames] of Object.entries(NOTIFICATION_CATEGORIES)) {
    const categoryItems: T[] = [];
    for (const name of typeNames) {
      const item = byName.get(name);
      if (item) {
        categoryItems.push(item);
        byName.delete(name);
      }
    }
    if (categoryItems.length > 0) {
      result.push({ category, items: categoryItems });
    }
  }

  // Remaining items go to General
  if (byName.size > 0) {
    const generalIdx = result.findIndex((r) => r.category === 'General');
    const remaining = Array.from(byName.values());
    if (generalIdx >= 0) {
      result[generalIdx].items.push(...remaining);
    } else {
      result.push({ category: 'General', items: remaining });
    }
  }

  return result;
}

// --- Component ---

const NotificationPreferenceMatrix: React.FC<
  NotificationPreferenceMatrixProps
> = ({
  preferences,
  onToggle,
  companySettings,
  onCompanySettingChange,
  disabled = false,
  readOnly = false,
  mode,
  userRole,
}) => {
  const { t } = useTranslation();

  // Category collapse state - all expanded by default
  const [collapsedCategories, setCollapsedCategories] = useState<
    Set<string>
  >(new Set());

  const toggleCategory = useCallback((category: string) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  }, []);

  // --- User mode helpers ---

  const showAllTypes = !userRole || userRole === 'Hr' || userRole === 'Admin';

  const userCategories = useMemo(() => {
    if (mode !== 'user' || !preferences) return [];
    const filtered = showAllTypes
      ? preferences
      : preferences.filter(p => !HR_ADMIN_ONLY_TYPES.has(p.notificationTypeName));
    return categorize(filtered);
  }, [mode, preferences, showAllTypes]);

  /** Compute master toggle state for user mode */
  const userMasterState = useMemo(() => {
    if (mode !== 'user' || !preferences) {
      return { email: false, push: false, inApp: false };
    }
    const channels: Record<Channel, boolean> = {
      email: true,
      push: true,
      inApp: true,
    };
    for (const pref of preferences) {
      for (const ch of CHANNELS) {
        const cp = pref[ch.key];
        // Only consider non-locked channels for master state
        if (!cp.locked && !cp.enabled) {
          channels[ch.key] = false;
        }
      }
    }
    return channels;
  }, [mode, preferences]);

  const handleUserMasterToggle = useCallback(
    (channel: Channel, value: boolean) => {
      if (!preferences || !onToggle) return;
      for (const pref of preferences) {
        const cp = pref[channel];
        if (!cp.locked && cp.enabled !== value) {
          onToggle(pref.notificationTypeId, channel, value);
        }
      }
    },
    [preferences, onToggle]
  );

  // --- Admin mode helpers ---

  const adminCategories = useMemo(() => {
    if (mode !== 'admin' || !companySettings) return [];
    return categorize(companySettings);
  }, [mode, companySettings]);

  /** Compute master toggle state for admin mode (inverted: enabled = !disabled) */
  const adminMasterState = useMemo(() => {
    if (mode !== 'admin' || !companySettings) {
      return { email: false, push: false, inApp: false };
    }
    const channels: Record<Channel, boolean> = {
      email: true,
      push: true,
      inApp: true,
    };
    for (const setting of companySettings) {
      if (setting.emailsDisabled) channels.email = false;
      if (setting.mobilePushNotificationsDisabled) channels.push = false;
      if (setting.inAppDisabled) channels.inApp = false;
    }
    return channels;
  }, [mode, companySettings]);

  const handleAdminMasterToggle = useCallback(
    (channel: Channel, value: boolean) => {
      if (!companySettings || !onCompanySettingChange) return;
      const field = adminFieldForChannel(channel);
      // value=true means enabled, so disabled=false
      for (const setting of companySettings) {
        const currentDisabled =
          setting[field as keyof CompanyNotificationSettingView] as boolean;
        const currentEnabled = !currentDisabled;
        if (currentEnabled !== value) {
          onCompanySettingChange(setting.notificationTypeId, field, !value);
        }
      }
    },
    [companySettings, onCompanySettingChange]
  );

  // --- Render helpers ---

  const getTypeLabel = (typeName: string): string => {
    // Indexed with the raw wire string, not NotificationTypeName: a server that
    // is a deploy ahead can send a name this bundle has never heard of, and the
    // raw-name fallback below is the honest degradation for that.
    const label = (
      NOTIFICATION_TYPE_LABELS as Record<string, string | undefined>
    )[typeName];
    // nsSeparator:false — the global i18n separator is ':', which truncated
    // '1:1 Check-In Updated' to a 'Check-In Updated' lookup in the '1' namespace.
    return label ? t(label, { nsSeparator: false }) : typeName;
  };

  // CO-375 / E13 — a lock always says WHY, so a greyed switch is never a mystery.
  const lockReason = (source: string): string => {
    switch (source) {
      case 'module':
        return t('notificationModules.lock.module');
      case 'inApp':
        return t('notificationModules.lock.inApp');
      default:
        return t('Disabled by your administrator');
    }
  };

  const renderUserRow = (pref: NotificationPreferenceView) => {
    // A locked channel is off whatever its stored value (E13 keeps the stored value for round trips).
    const effective = (cp: { enabled: boolean; locked: boolean }) => cp.enabled && !cp.locked;
    const allDisabled =
      !effective(pref.email) && !effective(pref.push) && !effective(pref.inApp);

    return (
      <div
        key={pref.notificationTypeId}
        className="flex items-center px-4 py-3 border-b border-border last:border-b-0 hover:bg-muted/30 transition-colors"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm text-foreground truncate">
              {getTypeLabel(pref.notificationTypeName)}
            </span>
            {allDisabled && (
              <span
                className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1"
                title={t(
                  'No notifications will be delivered through any channel'
                )}
              >
                <AppIcon
                  name="AlertTriangle"
                  size={14}
                  className="shrink-0"
                />
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-6 sm:gap-8">
          {CHANNELS.map((ch) => {
            const cp = pref[ch.key];
            return (
              <div
                key={ch.key}
                className="flex items-center gap-1.5 w-12 justify-center"
              >
                <Switch
                  checked={effective(cp)}
                  onCheckedChange={(val) =>
                    onToggle?.(pref.notificationTypeId, ch.key, val)
                  }
                  disabled={disabled || readOnly || cp.locked}
                  aria-label={`${getTypeLabel(pref.notificationTypeName)}: ${t(ch.label)}`}
                  data-testid={`notif-switch-${pref.notificationTypeId}-${ch.key}`}
                />
                {cp.locked && (
                  <span
                    role="img"
                    title={lockReason(cp.lockedBy ?? cp.source)}
                    aria-label={lockReason(cp.lockedBy ?? cp.source)}
                    data-testid={`notif-lock-${pref.notificationTypeId}-${ch.key}`}
                    data-lock-source={cp.lockedBy ?? cp.source}
                    className="inline-flex shrink-0"
                  >
                    <AppIcon
                      name="Lock"
                      size={14}
                      className="text-muted-foreground"
                    />
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderAdminRow = (setting: CompanyNotificationSettingView) => {
    return (
      <div
        key={setting.notificationTypeId}
        className="flex items-center px-4 py-3 border-b border-border last:border-b-0 hover:bg-muted/30 transition-colors"
      >
        <div className="flex-1 min-w-0">
          <span className="text-sm text-foreground truncate">
            {getTypeLabel(setting.notificationTypeName)}
          </span>
        </div>
        <div className="flex items-center gap-6 sm:gap-8">
          {CHANNELS.map((ch) => {
            const field = adminFieldForChannel(ch.key);
            const isDisabled =
              setting[
                field as keyof CompanyNotificationSettingView
              ] as boolean;
            // Switch ON = enabled (allowed), OFF = force-disabled
            return (
              <div
                key={ch.key}
                className="flex items-center w-12 justify-center"
              >
                <Switch
                  checked={!isDisabled}
                  onCheckedChange={(val) =>
                    onCompanySettingChange?.(
                      setting.notificationTypeId,
                      field,
                      !val
                    )
                  }
                  disabled={disabled || readOnly}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const categories =
    mode === 'user' ? userCategories : adminCategories;
  const masterState =
    mode === 'user' ? userMasterState : adminMasterState;
  const handleMasterToggle =
    mode === 'user' ? handleUserMasterToggle : handleAdminMasterToggle;

  if (categories.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col rounded-lg border border-border overflow-hidden">
      {/* Column headers + Master toggles */}
      <div className="flex items-center px-4 py-3 bg-muted/50 border-b border-border">
        <div className="flex-1 min-w-0">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            {mode === 'user'
              ? t('Notification Preferences')
              : t('Notification Management')}
          </span>
        </div>
        <div className="flex items-center gap-6 sm:gap-8">
          {CHANNELS.map((ch) => (
            <div
              key={ch.key}
              className="flex flex-col items-center gap-1.5 w-12"
            >
              <span className="text-xs font-medium text-muted-foreground">
                {t(ch.label)}
              </span>
              <Switch
                checked={masterState[ch.key]}
                onCheckedChange={(val) => handleMasterToggle(ch.key, val)}
                disabled={disabled || readOnly}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Category sections */}
      {categories.map(({ category, items }) => {
        const isCollapsed = collapsedCategories.has(category);

        return (
          <div key={category}>
            {/* Category header */}
            <button
              type="button"
              onClick={() => toggleCategory(category)}
              className={cn(
                'flex items-center w-full px-4 py-2.5 bg-muted/30 border-b border-border',
                'hover:bg-muted/50 transition-colors cursor-pointer text-start'
              )}
            >
              <AppIcon
                name="ChevronRight"
                size={16}
                className={cn(
                  'text-muted-foreground transition-transform duration-200 shrink-0 me-2',
                  // Collapsed points at the content (mirrored in RTL); expanded points down.
                  isCollapsed ? 'rtl:rotate-180' : 'rotate-90'
                )}
              />
              <span className="text-sm font-medium text-primary">
                {t(category, { nsSeparator: false })}
              </span>
              <span className="text-xs text-muted-foreground ms-2">
                ({items.length})
              </span>
            </button>

            {/* Category items */}
            {!isCollapsed && (
              <div>
                {items.map((item) =>
                  mode === 'user'
                    ? renderUserRow(item as NotificationPreferenceView)
                    : renderAdminRow(
                        item as CompanyNotificationSettingView
                      )
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default NotificationPreferenceMatrix;
